import React, { useState } from 'react';
import {
    Box,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
const StyledTextField = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        width: '100%',
        maxWidth: '320px',
        height: '35px',
        borderRadius: '4px',
        backgroundColor: '#F2F2F3',
        border: '1px solid #414958',
        padding: '0',
        '& fieldset': {
            border: 'none',
        },
        '&:hover fieldset': {
            border: 'none',
        },
        '&.Mui-focused fieldset': {
            border: 'none',
        },
    },
    '& .MuiOutlinedInput-input': {
        padding: '8px 16px',
    },
    '& .MuiInputLabel-outlined': {
        transform: 'translate(16px, 8px) scale(1)',
    },
    '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
        transform: 'translate(14px, -6px) scale(0.75)',
        backgroundColor: '#F2F2F3',
        padding: '0 4px',
    },
    [theme.breakpoints.down('md')]: {
        maxWidth: '100%',
    },
}));

const StyledSelect = styled(Select)(({ theme }) => ({
    width: '202px',
    height: '35px',
    borderRadius: '4px',
    backgroundColor: '#F2F2F3',
    border: '1px solid #414958',
    '& .MuiOutlinedInput-notchedOutline': {
        border: 'none',
    },
    '& .MuiSelect-select': {
        padding: '8px 16px',
    },
    [theme.breakpoints.down('md')]: {
        width: '100%',
    },
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
    '& .MuiInputLabel-outlined': {
        transform: 'translate(16px, 8px) scale(1)',
    },
    '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
        transform: 'translate(14px, -6px) scale(0.75)',
        backgroundColor: '#F2F2F3',
        padding: '0 4px',
    },
    [theme.breakpoints.down('md')]: {
        width: '100%',
    },
}));

export default function SearchFilter({
    filters = [],
    searchPlaceholder = "search",
    onSearchChange,
    onFilterChange
}) {
    const { t } = useTranslation();
    // State for search input
    const [searchValue, setSearchValue] = useState('');

    // Initialize state for each filter with empty values
    const [filterValues, setFilterValues] = useState(
        filters.reduce((acc, filter) => {
            acc[filter.id] = '';
            return acc;
        }, {})
    );

    // Handle search input changes
    const handleSearchChange = (event) => {
        const newValue = event.target.value;
        setSearchValue(newValue);
        if (onSearchChange) onSearchChange(newValue);
    };

    // Handle filter selection changes
    const handleFilterChange = (filterId, value) => {
        setFilterValues(prev => {
            const newValues = { ...prev, [filterId]: value };
            if (onFilterChange) onFilterChange(newValues);
            return newValues;
        });
    };

    return (
        <Box
            className="search-filter"
            sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                gap: '20px'
            }}
        >
            {/* Search TextField on the left */}
            <StyledTextField
                id="search-input"
                placeholder={t(searchPlaceholder)}
                variant="outlined"
                value={searchValue}
                onChange={handleSearchChange}
                sx={{
                    fontFamily: "Roboto, sans-serif",
                    fontWeight: 400,
                    fontSize: "16px",
                    lineHeight: "18.75px",
                    letterSpacing: "0%",
                    color: "#1F242E",
                    flex: "1",
                    "& input::placeholder": {
                        color: "#1F242E",
                        opacity: 1,
                    },
                }}
                InputProps={{
                    endAdornment: (
                        <SearchIcon sx={{color:"#183059",margin:"0px 10px"}}/>
                    )
                }}
            />

            {/* Filters container on the right */}
            <Box sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: '12px',
                flexWrap: 'wrap',
                justifyContent: 'flex-end'
            }}>
                {filters.map((filter) => (
                    <StyledFormControl key={filter.id}>
                        <InputLabel id={`${filter.id}-label`} sx={{
                            "& input::placeholder": {
                                color: "#1F242E",
                                opacity: 1,
                            },
                        }}>
                            {t(filter.label)}
                        </InputLabel>
                        <StyledSelect
                            labelId={`${filter.id}-label`}
                            id={`${filter.id}-select`}
                            value={filterValues[filter.id]}
                            label={filter.label}
                            onChange={(e) => handleFilterChange(filter.id, e.target.value)}
                        >
                            <MenuItem value="">
                                <em>{t("none")}</em>
                            </MenuItem>
                            {filter.options.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {t(option.label)}
                                </MenuItem>
                            ))}
                        </StyledSelect>
                    </StyledFormControl>
                ))}
            </Box>
        </Box>
    );
}